import { useState } from 'react';
import { MOTIVOS_CANCELAMENTO, MOTIVO_OUTRO } from '../vagas';

interface Props {
  nome: string;
  /** Nome do 1º suplente do grupo, quando entra alguém no lugar. */
  suplenteQueEntra: string | null;
  salvando: boolean;
  erro: string | null;
  aoConfirmar: (motivo: string, detalhe: string | null) => void;
  aoFechar: () => void;
}

// Folha de cancelamento: motivo obrigatório + observação opcional.
export default function FolhaCancelarInscricao({
  nome,
  suplenteQueEntra,
  salvando,
  erro,
  aoConfirmar,
  aoFechar,
}: Props) {
  const [motivo, setMotivo] = useState<string | null>(null);
  const [outro, setOutro] = useState('');
  const [observacao, setObservacao] = useState('');

  const outroValido = motivo !== MOTIVO_OUTRO || outro.trim().length > 0;
  const podeConfirmar = !!motivo && outroValido && !salvando;

  function confirmar() {
    if (!podeConfirmar || !motivo) return;
    const detalhe = motivo === MOTIVO_OUTRO ? outro.trim() : null;
    aoConfirmar(motivo, detalhe);
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-6">
      <div className="absolute inset-0 bg-black/30" onClick={aoFechar} aria-hidden />
      <div className="animate-modal-pop relative z-10 flex max-h-[88vh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3 border-b border-background-100 px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-[17px] font-bold text-foreground-950">
              Cancelar a inscrição de {nome.split(' ')[0]}?
            </h3>
            <p className="mt-0.5 text-[13px] text-foreground-600">
              Escolha o motivo. A vaga volta para a fila.
            </p>
          </div>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-2 gap-2">
            {MOTIVOS_CANCELAMENTO.map((m) => {
              const ativo = motivo === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMotivo(m)}
                  className={`min-h-[44px] cursor-pointer rounded-xl border px-3 py-2 text-left text-[13px] font-medium leading-snug transition-colors ${
                    ativo
                      ? 'border-danger-500 bg-danger-50 text-danger-700'
                      : 'border-secondary-200 bg-white text-foreground-800 hover:bg-background-50'
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>

          {motivo === MOTIVO_OUTRO && (
            <div className="mt-3">
              <label className="text-[13px] font-medium text-foreground-800" htmlFor="motivo-outro">
                Qual o motivo?
              </label>
              <input
                id="motivo-outro"
                type="text"
                value={outro}
                onChange={(e) => setOutro(e.target.value)}
                placeholder="Escreva o motivo"
                maxLength={120}
                className="mt-1 h-11 w-full rounded-xl border border-background-200 bg-white px-3 text-[15px] text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>
          )}

          <div className="mt-3">
            <label className="text-[13px] font-medium text-foreground-800" htmlFor="obs-cancelamento">
              Observação (opcional)
            </label>
            <textarea
              id="obs-cancelamento"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value.slice(0, 500))}
              rows={2}
              maxLength={500}
              placeholder="Alguma anotação para a equipe"
              className="mt-1 w-full resize-none rounded-xl border border-background-200 bg-white px-3 py-2 text-[15px] text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>

          {suplenteQueEntra && (
            <div className="mt-3 rounded-xl bg-background-100 px-3 py-2.5">
              <p className="text-[13px] text-foreground-700">
                No lugar dela entra:{' '}
                <strong className="font-semibold text-foreground-950">{suplenteQueEntra}</strong>
              </p>
            </div>
          )}

          {erro && (
            <p className="mt-3 rounded-xl bg-danger-50 px-3 py-2.5 text-[13px] text-danger-700">
              {erro}
            </p>
          )}
        </div>

        <div className="flex gap-2 border-t border-background-100 px-5 py-4">
          <button
            type="button"
            onClick={aoFechar}
            className="min-h-[46px] flex-1 cursor-pointer whitespace-nowrap rounded-full border border-secondary-300 bg-white px-4 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Voltar
          </button>
          <button
            type="button"
            onClick={confirmar}
            disabled={!podeConfirmar}
            className="min-h-[46px] flex-1 cursor-pointer whitespace-nowrap rounded-full bg-danger-600 px-4 text-[14px] font-semibold text-white transition-colors hover:bg-danger-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {salvando ? 'Cancelando…' : 'Cancelar inscrição'}
          </button>
        </div>
      </div>
    </div>
  );
}